// reviewSession.js — what one multi-patient recording attributed where, for
// the "What we heard" review on the Multi Recorder page.
//
// Two kinds of line:
//   * UNPLACED — the splitter could not tell whose visit it is, so the backend
//     committed it to NO transcript. This store is the only copy until the
//     clinician routes it, so it lives in localStorage (per user): the last
//     chunk's upload can finish after the page unmounts, and a reload must not
//     lose it. Namespaced per user because nothing in logout clears unknown
//     keys and admin impersonation switches user without a reload.
//   * PLACED — already on a visit's transcript. Kept in memory only, purely so
//     the review can show the whole recording and let the clinician move or
//     drop a line. Nothing is lost if this goes (reload): the text is on the
//     visit already, the review just shows less context.

const CHANGE_EVENT = 'comprehendpt:multirecord-review';

/** A chosen "visit" meaning: this line belongs in no note. */
export const IGNORE = 'ignore';

export const unplacedStorageKey = (email) =>
  `comprehendpt_multirecord_unplaced:${String(email || 'anon').toLowerCase()}`;

// Used only when localStorage refuses a write (quota, blocked storage), so the
// only copy of a line is never dropped silently. Lost on reload in that case.
const memoryFallback = {};
// key -> placed lines of the current recording (see header).
const placedByUser = {};

let idCounter = 0;
const nextId = () => {
  idCounter += 1;
  return `${Date.now().toString(36)}-${idCounter}-${Math.random().toString(36).slice(2, 8)}`;
};
// Spoken order across chunks: a chunk's lines get consecutive numbers.
let seqCounter = 0;

export function readUnplaced(email) {
  const fallback = memoryFallback[unplacedStorageKey(email)];
  if (fallback) return fallback;
  try {
    const raw = window.localStorage.getItem(unplacedStorageKey(email));
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed)
      ? parsed.filter(
          (line) =>
            line &&
            typeof line.text === 'string' &&
            Array.isArray(line.candidates),
        )
      : [];
  } catch (e) {
    return [];
  }
}

const notify = () => window.dispatchEvent(new Event(CHANGE_EVENT));

function writeUnplaced(email, lines) {
  const key = unplacedStorageKey(email);
  try {
    delete memoryFallback[key];
    if (lines.length) {
      window.localStorage.setItem(key, JSON.stringify(lines));
    } else {
      window.localStorage.removeItem(key);
    }
  } catch (e) {
    console.error('[MultiRecorder] could not save unplaced lines', e);
    memoryFallback[key] = lines;
  }
  notify();
}

export const readPlaced = (email) => placedByUser[unplacedStorageKey(email)] || [];

function writePlaced(email, lines) {
  placedByUser[unplacedStorageKey(email)] = lines;
  notify();
}

/** Forget the placed lines of the last recording (a new one is starting). */
export function clearPlaced(email) {
  if (readPlaced(email).length) writePlaced(email, []);
}

/** An unplaced line nobody has routed (or dismissed) yet. */
export const needsChoice = (line) => !Array.isArray(line.targets);

/** The visit a placed line ends up on: moved, else where the backend put it. */
export const ownerOf = (line) => (line.choice != null ? line.choice : line.from);

export const bestGuessOf = (line) =>
  line.candidates.includes(line.bestGuess) ? line.bestGuess : null;

export const suggestedOwner = (line) =>
  line.confidence === 'needs_review' ? bestGuessOf(line) : null;

/** An unplaced line's routing: the clinician's pick, else small talk ("Okay.") goes in no note. */
export const choiceOf = (line) => (line.choice == null && line.nonclinical ? IGNORE : line.choice);

/** Save AI hints without changing a clinician's choices or the source wording. */
export function recordAttributionHints(email, hints) {
  const update = (line) => {
    const hint = hints[line.id];
    if (!hint) return line;
    // The summarizer gives nonclinical speech an empty summary, usually with no
    // patient guess; it is still small talk, so it must not land in the pile.
    const nonclinical = hint.summary === '';
    if (!line.candidates.includes(hint.bestGuess) ||
      !['confident', 'needs_review', 'unplaced'].includes(hint.confidence)) return nonclinical ? { ...line, nonclinical } : line;
    return { ...line, bestGuess: hint.bestGuess, confidence: hint.confidence, nonclinical };
  };
  writeUnplaced(email, readUnplaced(email).map(update));
  writePlaced(email, readPlaced(email).map(update));
}

/**
 * Where a line sits in the backend's corpus row, so its review outcome can be
 * checked against what the segmenter really said. Only segmenter uploads that
 * asked for unknowns carry this; anything else is simply not reported.
 */
function sourceOf(data, index) {
  const sessionId = data?.recordingSessionId;
  const seq = data?.chunkSeq;
  if (typeof sessionId !== 'string' || !sessionId || !Number.isInteger(seq) || !Number.isInteger(index)) {
    return null;
  }
  return { sessionId, seq, index };
}

/** The placed text of one upload, whichever endpoint it came from. */
function placedOf(data) {
  if (Array.isArray(data?.utterances)) return data.utterances;
  // Classic split: one block per visit, newline separated, not in spoken order.
  return Object.entries(data?.transcripts || {}).flatMap(([conversationId, text]) =>
    String(text || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => ({ conversationId, text: line })),
  );
}

/**
 * Record one chunk's result. `targets` are the visits the chunk was recorded
 * under — the only visits an unplaced line can belong to. `names`
 * (conversationId -> label) is kept with each line because the page's visit
 * list may not be loaded (or may be filtered) when the review reopens.
 */
export function addChunk(email, data, targets, spokenAt, names = {}) {
  const visitIds = (targets || []).map(Number).filter(Number.isFinite);
  if (!visitIds.length) return;
  const at = Number(spokenAt) || Date.now();
  const lineNames = Object.fromEntries(
    visitIds.filter((id) => names[id]).map((id) => [id, names[id]]),
  );
  const placed = placedOf(data);
  const unplaced = data?.unplaced || [];
  const base = seqCounter;
  seqCounter += placed.length + unplaced.length + 1;

  const newPlaced = placed
    .map((u, index) => ({
      id: nextId(),
      text: String(u.text || '').trim(),
      from: Number(u.conversationId),
      choice: null,
      removed: false,
      candidates: visitIds,
      names: lineNames,
      spokenAt: at,
      orderApproximate: Boolean(data.orderApproximate) || !Array.isArray(data.utterances),
      seq: base + index,
      source: sourceOf(data, u.index),
    }))
    .filter((line) => line.text && Number.isFinite(line.from));
  if (newPlaced.length) writePlaced(email, [...readPlaced(email), ...newPlaced]);

  const newUnplaced = unplaced
    .map((item) => ({
      id: nextId(),
      text: String(item?.text || '').trim(),
      candidates: visitIds,
      names: lineNames,
      spokenAt: at,
      orderApproximate: Boolean(data.orderApproximate) || !Array.isArray(data.utterances),
      // Just before the placed utterance it was spoken ahead of.
      seq: base + (Number.isFinite(item?.after) ? item.after : placed.length) - 0.5,
      choice: null,
      source: sourceOf(data, item?.index),
    }))
    .filter((line) => line.text);
  if (newUnplaced.length) writeUnplaced(email, [...readUnplaced(email), ...newUnplaced]);
}

/** Route an unplaced line to a visit, or IGNORE it. */
export function chooseVisit(email, lineId, choice) {
  writeUnplaced(
    email,
    readUnplaced(email).map((line) => (line.id === lineId ? { ...line, choice } : line)),
  );
}

/** Move an already-placed line to another visit (its own visit undoes it). */
export function movePlaced(email, lineId, conversationId) {
  writePlaced(
    email,
    readPlaced(email).map((line) =>
      line.id === lineId
        ? {
            ...line,
            removed: false,
            confirmed: true,
            choice: conversationId === line.from ? null : conversationId,
          }
        : line,
    ),
  );
}

/** Take an already-placed line out of every note, or put it back. */
export function removePlaced(email, lineId, removed = true) {
  writePlaced(
    email,
    readPlaced(email).map((line) =>
      line.id === lineId ? { ...line, removed, choice: removed ? null : line.choice } : line,
    ),
  );
}

export function subscribeReview(email, callback) {
  const onChange = () => callback({ unplaced: readUnplaced(email), placed: readPlaced(email) });
  const onStorage = (event) => {
    if (event.key === unplacedStorageKey(email)) onChange();
  };
  window.addEventListener(CHANGE_EVENT, onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(CHANGE_EVENT, onChange);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * Read-modify-write one visit's transcript through the existing edit endpoint.
 * The page only opens the review once its own uploads have drained, but a
 * chunk from another tab or device may still be in flight: its handler holds a
 * stale copy of the whole item and would overwrite this edit. While the visit
 * reads as transcribing, refuse, and the caller leaves the change for later.
 */
async function editTranscript(apiClient, conversationId, change) {
  const details = await apiClient.getConversationDetails(conversationId);
  if (!details || details.error) throw new Error(details?.error || 'no details');
  if (String(details.status || '').startsWith('Transcribing')) {
    throw new Error('visit is still transcribing');
  }
  const next = change(details.transcript || '');
  const result = await apiClient.editConversation(conversationId, 'transcript', next);
  if (result && result.error) throw new Error(result.error);
}

/** Drop one occurrence of `text` as a whole line, the latest one. Null if absent. */
export function withoutLine(transcript, text) {
  const lines = transcript.split('\n');
  for (let i = lines.length - 1; i >= 0; i -= 1) {
    if (lines[i].trim() === text.trim()) {
      lines.splice(i, 1);
      return lines.join('\n');
    }
  }
  return null;
}

const hintsOf = (line) => ({
  bestGuess: bestGuessOf(line) != null ? String(bestGuessOf(line)) : null,
  confidence: line.confidence || null,
  nonclinical: line.nonclinical === true,
});

/**
 * What the review decided for each line, and whether a clinician decided it or
 * the AI's default was applied because nobody touched the line. The visit
 * transcripts show where lines ended up; only this can tell those apart.
 * `unplaced` pairs each line as it was before this review resolved it with how
 * it was resolved. Lines without a corpus `source` are left out.
 */
export function reviewOutcomeLines(unplaced, placed) {
  const outcome = (line, decision, ai, final) => ({
    ...line.source,
    ai,
    final: final == null ? null : String(final),
    decision,
    ...hintsOf(line),
  });
  const lines = [];
  unplaced.forEach(({ before, after }) => {
    if (!before.source || !needsChoice(before) || needsChoice(after)) return;
    const target = after.targets[0];
    if (before.choice === IGNORE) lines.push(outcome(before, 'ignored', 'unknown', null));
    else if (before.choice != null) lines.push(outcome(before, 'routed', 'unknown', target));
    else if (target == null) lines.push(outcome(before, 'auto_ignored', 'unknown', null));
    else lines.push(outcome(before, 'auto_routed', 'unknown', target));
  });
  placed.forEach((line) => {
    if (!line.source) return;
    const ai = String(line.from);
    if (line.removed) lines.push(outcome(line, 'removed', ai, null));
    else if (line.choice != null && line.choice !== line.from) lines.push(outcome(line, 'moved', ai, line.choice));
    else lines.push(outcome(line, line.confirmed ? 'confirmed' : 'kept', ai, line.from));
  });
  return lines;
}

/** Analytics only: never delays or fails the review. */
function reportOutcome(apiClient, finish, lines) {
  if (!lines.length || typeof apiClient?.saveMultiRecordReviewOutcome !== 'function') return;
  Promise.resolve()
    .then(() => apiClient.saveMultiRecordReviewOutcome(finish, lines))
    .catch((error) => console.warn('[MultiRecorder] review outcome not recorded', error));
}

/**
 * Apply the review. Unplaced lines go to their chosen visit; `dismissed` (the
 * clinician closed it) sends a line with no choice to the AI's best guess.
 * Tentative suggestions also apply on Done. Moved placed lines are appended to the new visit
 * FIRST and only then removed from the old one, so a failure part-way leaves a
 * duplicate, never a loss. Returns the visit ids that could not be written.
 *
 * Lines are appended at the end of a transcript, not where they were spoken.
 */
export async function applyReview(apiClient, email, { dismissed = false, retryOnly = false } = {}) {
  if (dismissed && readUnplaced(email).some((line) =>
    needsChoice(line) && choiceOf(line) == null && bestGuessOf(line) == null,
  )) {
    throw new Error('An AI suggestion is unavailable. Choose a patient for the remaining lines before closing.');
  }
  // Resolve unplaced lines and save that BEFORE any write, so an interrupted
  // write is retried later (retryPlacement) instead of asked again.
  const unresolved = readUnplaced(email);
  const resolved = unresolved.map((line) => {
    if (!needsChoice(line) || retryOnly) return line;
    if (choiceOf(line) === IGNORE) return { ...line, targets: [] };
    if (line.choice != null) return { ...line, targets: [line.choice] };
    const guess = dismissed ? bestGuessOf(line) : suggestedOwner(line);
    if (guess != null) return { ...line, targets: [guess] };
    return line;
  });
  writeUnplaced(email, resolved);
  const placedLines = readPlaced(email);
  // A retry only finishes writes; their decisions were reported the first time.
  const outcome = retryOnly ? [] : reviewOutcomeLines(
    unresolved.map((before, i) => ({ before, after: resolved[i] })),
    placedLines,
  );

  // Phase 1: appends (routed unplaced lines + moved placed lines).
  const arrivals = new Map();
  const arrive = (conversationId, line) => {
    if (!arrivals.has(conversationId)) arrivals.set(conversationId, []);
    arrivals.get(conversationId).push(line);
  };
  resolved
    .filter((line) => !needsChoice(line))
    .forEach((line) => line.targets.forEach((id) => arrive(id, line)));
  placedLines
    .filter((line) => !line.removed && line.choice != null && line.choice !== line.from)
    .forEach((line) => arrive(line.choice, line));

  const failed = new Set();
  const appendedTo = new Set();
  for (const [conversationId, lines] of arrivals) {
    const entries = [...lines]
      .sort((a, b) => a.spokenAt - b.spokenAt || a.seq - b.seq)
      .map(({ id, text }) => ({ id, text }));
    try {
      const result = await apiClient.appendMultiRecordReviewLines(conversationId, entries);
      if (result?.error) throw new Error(result.error);
      appendedTo.add(conversationId);
    } catch (error) {
      console.error(`[MultiRecorder] could not add lines to visit ${conversationId}`, error);
      failed.add(conversationId);
    }
  }

  // Phase 2: removals (dropped lines, and moved lines that safely arrived).
  const leaving = placedLines.filter(
    (line) => line.removed || (line.choice != null && appendedTo.has(line.choice)),
  );
  const removedIds = new Set();
  const notFound = new Set();
  const bySource = new Map();
  leaving.forEach((line) => {
    if (!bySource.has(line.from)) bySource.set(line.from, []);
    bySource.get(line.from).push(line);
  });
  for (const [conversationId, lines] of bySource) {
    try {
      await editTranscript(apiClient, conversationId, (t) => {
        let next = t;
        lines.forEach((line) => {
          const without = withoutLine(next, line.text);
          // Edited away already — nothing left to remove, so count it as done.
          if (without == null) notFound.add(line.id);
          else next = without;
          removedIds.add(line.id);
        });
        return next;
      });
    } catch (error) {
      console.error(`[MultiRecorder] could not remove lines from visit ${conversationId}`, error);
      lines.forEach((line) => removedIds.delete(line.id));
      failed.add(conversationId);
    }
  }
  // What was decided, even where a write is still pending: the retry carries
  // out the same decision, so it is reported once, now.
  reportOutcome(apiClient, dismissed ? 'skip' : 'done', outcome);
  if (notFound.size) {
    console.warn(
      `[MultiRecorder] ${notFound.size} line(s) were no longer in their visit's transcript; left as is`,
    );
  }

  // Unplaced: drop the visits that were written. Re-read: a chunk may have
  // added new lines while the writes were in flight.
  const resolvedIds = new Set(resolved.filter((l) => !needsChoice(l)).map((l) => l.id));
  writeUnplaced(
    email,
    readUnplaced(email)
      .map((line) =>
        resolvedIds.has(line.id)
          ? { ...line, targets: line.targets.filter((id) => !appendedTo.has(id)) }
          : line,
      )
      .filter((line) => needsChoice(line) || line.targets.length),
  );
  // Placed: keep only changes still to do. A moved line whose append landed but
  // whose removal failed is now a plain removal from its old visit.
  writePlaced(
    email,
    readPlaced(email)
      .filter((line) => !removedIds.has(line.id))
      .filter((line) => line.removed || line.choice != null)
      .map((line) =>
        line.choice != null && appendedTo.has(line.choice)
          ? { ...line, choice: null, removed: true }
          : line,
      ),
  );
  return [...failed];
}

/** Finish writes a previous review could not complete. */
export function retryPlacement(apiClient, email) {
  if (!readUnplaced(email).some((line) => !needsChoice(line))) {
    return Promise.resolve([]);
  }
  return applyReview(apiClient, email, { retryOnly: true });
}
